import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  IconButton,
  Grid,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Paper,
  InputAdornment,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  CalendarToday as CalendarIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Phone as PhoneIcon,
  LocalOffer as DiscountIcon,
  Percent as PercentIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import TICKET_TYPE from '../../../constants/ticketTypes';
import PAYMENT_STATUS, { getPaymentStatus } from '../../../constants/paymentStatus';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { generateRepairTicketPreview } from '../utils/ticketPreviewUtils';
import { useStoreInfo } from '../../../hooks/useCompanyInfo';
import * as documentNoteTemplateService from '../../companysettings/services/documentNoteTemplateService';
import MultiplePaymentMethodsSection from '../../invoices/components/MultiplePaymentMethodsSection';
import { DocumentType, getDefaultTemplateId, getTemplateContent } from '../../../utils/documentNoteTemplateUtils';
import { calcTotals as sharedCalcTotals, calcOriginalTotal } from '../utils/totalsCalculator';

const REPAIR_SERVICE_GROUPS = [
  {
    icon: '📱',
    label: { en: 'Screen', fr: 'Écran', nl: 'Scherm' },
    items: [
      { en: 'Screen / Glass / Touchscreen', fr: 'Écran / Vitre / Tactile', nl: 'Scherm / Glas / Touchscreen' },
      { en: 'Rear Glass + Frame', fr: 'Vitre arrière + Châssis', nl: 'Achterglas + Frame' },
      { en: 'Screen Protector', fr: "Protection d'écran", nl: 'Screenprotector' },
    ],
  },
  {
    icon: '🔋',
    label: { en: 'Battery & Charging', fr: 'Batterie & Charge', nl: 'Batterij & Opladen' },
    items: [
      { en: 'Battery', fr: 'Batterie', nl: 'Batterij' },
      { en: 'Charging Port', fr: 'Port de charge', nl: 'Oplaadpoort' },
      { en: 'Wireless Charging', fr: 'Charge sans fil', nl: 'Draadloos opladen' },
      { en: 'Charging IC', fr: 'IC de charge', nl: 'Oplaad-IC' },
    ],
  },
  {
    icon: '📷',
    label: { en: 'Camera', fr: 'Caméra', nl: 'Camera' },
    items: [
      { en: 'Rear Camera', fr: 'Caméra arrière', nl: 'Achtercamera' },
      { en: 'Rear Camera Lens', fr: 'Lentille caméra arrière', nl: 'Achtercameralens' },
      { en: 'Front Camera', fr: 'Caméra avant', nl: 'Frontcamera' },
      { en: 'Flash', fr: 'Flash', nl: 'Flits' },
    ],
  },
  {
    icon: '🔊',
    label: { en: 'Audio', fr: 'Audio', nl: 'Audio' },
    items: [
      { en: 'Speaker', fr: 'Haut-parleur', nl: 'Luidspreker' },
      { en: 'Receiver / Earpiece', fr: 'Écouteur interne', nl: 'Oorluidspreker' },
      { en: 'Microphone', fr: 'Microphone', nl: 'Microfoon' },
      { en: 'Headphone Jack', fr: 'Prise casque', nl: 'Koptelefoonaansluiting' },
    ],
  },
  {
    icon: '🔘',
    label: { en: 'Buttons & Sensors', fr: 'Boutons & Capteurs', nl: 'Knoppen & Sensoren' },
    items: [
      { en: 'Power / Volume / Mute Buttons', fr: 'Boutons Power / Volume / Silence', nl: 'Aan-uit- / Volume- / Muteknoppen' },
      { en: 'Home Button', fr: 'Bouton Home', nl: 'Home-knop' },
      { en: 'Proximity Sensor', fr: 'Capteur de proximité', nl: 'Nabijheidssensor' },
      { en: 'Face ID / Touch ID / Fingerprint', fr: 'Face ID / Touch ID / Empreinte', nl: 'Face ID / Touch ID / Vingerafdruk' },
      { en: 'Vibration', fr: 'Vibreur', nl: 'Trilfunctie' },
    ],
  },
  {
    icon: '📡',
    label: { en: 'Connectivity', fr: 'Connectivité', nl: 'Connectiviteit' },
    items: [
      { en: 'WiFi / GSM / GPS / Bluetooth Antenna', fr: 'Antenne WiFi / GSM / GPS / Bluetooth', nl: 'WiFi- / GSM- / GPS- / Bluetooth-antenne' },
      { en: 'NFC', fr: 'NFC', nl: 'NFC' },
      { en: 'Network / Signal Repair', fr: 'Réparation réseau / signal', nl: 'Netwerk- / signaalreparatie' },
    ],
  },
  {
    icon: '🧩',
    label: { en: 'Hardware', fr: 'Matériel', nl: 'Hardware' },
    items: [
      { en: 'Motherboard / Logic Board Repair', fr: 'Réparation carte mère', nl: 'Moederbordreparatie' },
      { en: 'Water / Liquid Damage', fr: 'Dégât des eaux / liquide', nl: 'Water- / vloeistofschade' },
      { en: 'Connector Repair', fr: 'Réparation de connecteur', nl: 'Connectorreparatie' },
      { en: 'IC / Microsoldering Repair', fr: 'Réparation IC / microsoudure', nl: 'IC- / microsoldeerreparatie' },
    ],
  },
  {
    icon: '💾',
    label: { en: 'Software & Data', fr: 'Logiciel & Données', nl: 'Software & Data' },
    items: [
      { en: 'Data Transfer', fr: 'Transfert de données', nl: 'Gegevensoverdracht' },
      { en: 'Data Recovery', fr: 'Récupération de données', nl: 'Gegevensherstel' },
      { en: 'Software Repair / Update', fr: 'Réparation / mise à jour logicielle', nl: 'Softwarereparatie / update' },
      { en: 'Factory Reset / Setup', fr: 'Réinitialisation / configuration', nl: 'Fabrieksreset / installatie' },
    ],
  },
  {
    icon: '🧼',
    label: { en: 'Maintenance', fr: 'Entretien', nl: 'Onderhoud' },
    items: [
      { en: 'Cleaning / Disinfection', fr: 'Nettoyage / Désinfection', nl: 'Reiniging / Desinfectie' },
      { en: 'Charging Port Cleaning', fr: 'Nettoyage du port de charge', nl: 'Reiniging oplaadpoort' },
    ],
  },
  {
    icon: '🛠️',
    label: { en: 'Other Repair / Diagnostic', fr: 'Autre réparation / diagnostic', nl: 'Andere reparatie / diagnostiek' },
    isOther: true,
    items: [{ en: 'Other Repair / Diagnostic', fr: 'Autre réparation / diagnostic', nl: 'Andere reparatie / diagnostiek' }],
  },
];

// Falls back to English when the active language has no entry
function localize(value, lang) {
  return value[lang] || value.en;
}

// Strip HTML tags from template content
function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').trim();
}

// Section Header Component
function SectionHeader({ icon: Icon, label, color = 'primary.main', action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
      <Icon sx={{ fontSize: 15, color }} />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          fontSize: '0.63rem',
        }}
      >
        {label}
      </Typography>
      {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
    </Box>
  );
}

function RepairTicketDialog({
  open,
  onClose,
  onSubmit,
  paymentMethods = [],
  vatRates = [],
  customer,
  onOpenCustomerDialog,
  container,
  storeInfo: propStoreInfo,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { storeInfo: hookStoreInfo } = useStoreInfo();
  const storeInfo = propStoreInfo || hookStoreInfo;
  const lang = (i18n.language || 'en').slice(0, 2).toLowerCase();
  const vatRateOptions = useMemo(() => {
    const configuredRates = vatRates.length > 0 ? vatRates : [0, 6, 12, 21];
    return [...new Set(configuredRates
      .map((rate) => typeof rate === 'number' ? rate : Number(rate?.VATPercentage ?? rate?.vatPercentage))
      .filter((rate) => Number.isFinite(rate))
    )].sort((a, b) => a - b);
  }, [vatRates]);

  // Flat, grouped option list for the searchable service picker
  const serviceOptions = useMemo(
    () =>
      REPAIR_SERVICE_GROUPS.flatMap((group) => {
        const groupName = localize(group.label, lang);
        return group.items.map((item) => ({
          label: localize(item, lang),
          group: groupName,
          isOther: !!group.isOther,
        }));
      }),
    [lang]
  );

  const groupIcons = useMemo(
    () =>
      REPAIR_SERVICE_GROUPS.reduce((acc, group) => {
        acc[localize(group.label, lang)] = group.icon;
        return acc;
      }, {}),
    [lang]
  );

  // Customer state
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  
  // Form state
  const [pickupDate, setPickupDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 3);
    date.setHours(12, 0, 0, 0); // Default to 12:00 PM
    return date.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm format for datetime-local
  });
  const [services, setServices] = useState([]);
  
  // Current service being added
  const [selectedServiceOption, setSelectedServiceOption] = useState(null);
  const [currentService, setCurrentService] = useState({
    customName: '',
    model: '',
    serialNumber: '',
    price: '',
    quantity: 1,
  });
  
  // Payment state
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [paidAmount, setPaidAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [useMultiplePayments, setUseMultiplePayments] = useState(false);
  const [localPMArray, setLocalPMArray] = useState([]);
  const [pmError, setPmError] = useState(false);
  
  // Notes template state
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [selectedNoteTemplate, setSelectedNoteTemplate] = useState('');
  
  // Service validation state
  const [serviceError, setServiceError] = useState('');
  
  // Confirmation dialog state
  const [confirmUnpaidDialog, setConfirmUnpaidDialog] = useState({
    open: false,
    onConfirm: null,
  });

  // TVA and discount state
  const [isTvaIncluded, setIsTvaIncluded] = useState(true);
  const [customVATRate, setCustomVATRate] = useState(21);
  const [discount, setDiscount] = useState(null);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [discountInputVal, setDiscountInputVal] = useState('');
  const [discountInputType, setDiscountInputType] = useState('percentage');

  // Preview state
  const [iframeHeight, setIframeHeight] = useState(600);
  const [receiptNumber, setReceiptNumber] = useState('');
  
  // Quotation mode state
  const [isQuotationMode, setIsQuotationMode] = useState(false);

  // Generate receipt number on open
  useEffect(() => {
    if (open) {
      const timestamp = Date.now();
      const randomPart = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      setReceiptNumber(`REP-${timestamp}-${randomPart}`);
    }
  }, [open]);
  
  // Fetch note templates and categories on open
  useEffect(() => {
    if (!open) return;
    
    // Skip if templates already loaded
    if (noteTemplates.length === 0) {
      documentNoteTemplateService.getTemplatesByType('RepairTicket')
        .then((data) => {
          const allTemplates = data?.payload || data || [];
          // Filter templates by DocumentType.RepairTicket (1)
          const templates = allTemplates.filter(t => 
            t.DocumentType === DocumentType.RepairTicket || 
            t.DocumentType === DocumentType.All
          );
          setNoteTemplates(templates);

          // Auto-select default or first template
          if (templates.length > 0) {
            const defaultTemplate = templates.find(t => t.IsDefault) || templates[0];
            setSelectedNoteTemplate(defaultTemplate.Id);
            const plainText = stripHtml(defaultTemplate.NoteContent);
            setNotes(plainText.slice(0, 1000));
          }
        })
        .catch(() => {});
    } else {
      // Templates already loaded, auto-select default or first template
      if (noteTemplates.length > 0) {
        const defaultTemplate = noteTemplates.find(t => t.IsDefault) || noteTemplates[0];
        setSelectedNoteTemplate(defaultTemplate.Id);
        const plainText = stripHtml(defaultTemplate.NoteContent);
        setNotes(plainText.slice(0, 1000));
      }
    }
  }, [open]);

  // Auto-select template when dialog opens and templates are available
  useEffect(() => {
    if (open && noteTemplates.length > 0) {
      const defaultTemplate = noteTemplates.find(t => t.IsDefault) || noteTemplates[0];
      setSelectedNoteTemplate(defaultTemplate.Id);
      const plainText = stripHtml(defaultTemplate.NoteContent);
      setNotes(plainText.slice(0, 1000));
    }
  }, [open, noteTemplates]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      const date = new Date();
      date.setDate(date.getDate() + 3);
      setPickupDate(date.toISOString().slice(0, 10));
      setServices([]);
      setSelectedServiceOption(null);
      setCurrentService({ customName: '', model: '', serialNumber: '', price: '', quantity: 1 });
      setSelectedPaymentMethod(null);
      setPaidAmount('');
      setServiceError(''); // Clear service error
      setIsTvaIncluded(true);
      setCustomVATRate(21);
      setDiscount(null);
      setShowDiscountForm(false);
      setDiscountInputVal('');
      setIframeHeight(600);
      setUseMultiplePayments(false);
      setLocalPMArray([]);
      setPmError(false);
      setIsQuotationMode(false);
      
      // Pre-fill customer info if customer is selected
      if (customer) {
        setCustomerName(customer.Name || '');
        setCustomerPhone(customer.PhoneNumber || customer.Phone || '');
      } else {
        setCustomerName('');
        setCustomerPhone('');
      }
    }
  }, [open, customer]);

  // Reset iframe height when services change
  useEffect(() => {
    setIframeHeight(600);
  }, [services.length]);

  // Calculate totals using shared calculator
  const { subtotal, discountAmount, totalVAT, totalIncl, subtotalExclVAT, originalTotal } = useMemo(() => {
    // Transform services to cart format for calculation
    const servicesAsCart = services.map(service => ({
      Quantity: parseInt(service.quantity) || 1,
      Price: parseFloat(service.price) || 0,
      PriceInclVAT: parseFloat(service.price) || 0,
      VAT: customVATRate
    }));
    
    const totals = sharedCalcTotals(servicesAsCart, discount, customVATRate, isTvaIncluded);
    
    return {
      subtotal: totals.subtotal,
      discountAmount: totals.discountAmount,
      totalVAT: totals.totalVAT,
      totalIncl: totals.totalIncl,
      subtotalExclVAT: totals.subtotal,
      originalTotal: totals.originalTotal
    };
  }, [services, discount, customVATRate, isTvaIncluded]);

  const paid = parseFloat(paidAmount) || 0;
  const remaining = Math.max(0, totalIncl - paid);

  // Calculate payment status using PAYMENT_STATUS constant
  const paymentStatusValue = getPaymentStatus(paid, totalIncl);
  const paymentStatus = {
    label: paymentStatusValue,
    color: paymentStatusValue === PAYMENT_STATUS.PAID ? 'success' :
           paymentStatusValue === PAYMENT_STATUS.PARTIAL_PAID ? 'warning' : 'error'
  };

  // Generate preview HTML
  const previewHtml = useMemo(() => {
    if (!storeInfo) {
      return `<html><body style="font-family:sans-serif;padding:40px 20px;text-align:center;color:#9e9e9e"><p style="font-size:14px">${t('loading_preview') || 'Loading preview...'}</p></body></html>`;
    }
    
    // Transform services to cart format (empty array if no services)
    // When VAT is included, convert price to base price (excl VAT)
    const cart = services.map(s => {
      const originalPrice = parseFloat(s.price) || 0;
      const basePrice = isTvaIncluded && originalPrice > 0 
        ? originalPrice / (1 + customVATRate / 100) 
        : originalPrice;
      
      return {
        Name: s.name,
        SerialNumber: s.serialNumber,
        Quantity: s.quantity,
        Price: basePrice,
        PriceInclVAT: isTvaIncluded ? originalPrice : undefined,
        VAT: customVATRate,
      };
    });

    return generateRepairTicketPreview({
      cart,
      customerName,
      customerPhone,
      storeInfo,
      receiptNumber,
      paidAmount,
      paymentMethod: selectedPaymentMethod,
      allPaymentMethods: paymentMethods,
      discount,
      customVATRate,
      notes: notes || '',
      pickupDate,
      isTvaIncluded,
      isQuotation: isQuotationMode,
    });
  }, [
    storeInfo, services, customer, customerName, customerPhone, selectedPaymentMethod,
    paidAmount, receiptNumber, discount, customVATRate, notes, paymentMethods, pickupDate, isTvaIncluded, isQuotationMode, t
  ]);

  // Handle repair service selection
  const handleServiceSelect = (event, option) => {
    setSelectedServiceOption(option);
    if (!option?.isOther) {
      setCurrentService((prev) => ({ ...prev, customName: '' }));
    }
    setServiceError('');
  };

  // Handle phone model input
  const handlePhoneModelChange = (event) => {
    setCurrentService({ ...currentService, model: event.target.value });
    setServiceError('');
  };
  
  // Handle note template selection
  const handleNoteTemplateChange = (event) => {
    const templateId = event.target.value;
    setSelectedNoteTemplate(templateId);
    
    if (!templateId) {
      setNotes('');
      return;
    }
    
    const template = noteTemplates.find(t => t.Id === templateId);
    if (template) {
      const plainText = stripHtml(template.NoteContent);
      setNotes(plainText.slice(0, 1000));
    }
  };

  // Add service
  const handleAddService = () => {
    // Clear previous error
    setServiceError('');
    
    // Validate service selection
    if (!selectedServiceOption) {
      setServiceError(t('service_required') || 'Please select a service');
      return;
    }

    const serviceName = selectedServiceOption.isOther
      ? currentService.customName.trim()
      : selectedServiceOption.label;

    if (!serviceName) {
      setServiceError(t('service_name_required') || 'Service name is required');
      return;
    }

    // Validate phone model
    if (!currentService.model.trim()) {
      setServiceError(t('phone_model_required') || 'Phone model is required');
      return;
    }

    // A duplicate is only the same service for the same phone model.
    const phoneModel = currentService.model.trim();
    const normalizedServiceName = serviceName.toLowerCase().trim();
    const normalizedPhoneModel = phoneModel.toLowerCase();
    const serviceLabel = `${serviceName} - ${phoneModel}`;

    const isDuplicate = services.some(s =>
      (s.serviceName || s.name.split(' - ')[0]).toLowerCase().trim() === normalizedServiceName &&
      (s.phoneModel || s.name.split(' - ').slice(1).join(' - ')).toLowerCase().trim() === normalizedPhoneModel
    );
    if (isDuplicate) {
      setServiceError(t('service_already_added') || 'This service is already added to the list');
      return;
    }
    
    // Validate price (optional in quotation mode)
    if (!isQuotationMode && (!currentService.price || parseFloat(currentService.price) <= 0)) {
      setServiceError(t('service_price_required') || 'Service price is required');
      return;
    }

    const newService = {
      id: Date.now(),
      serviceName,
      phoneModel,
      name: serviceLabel,
      serialNumber: currentService.serialNumber.trim(),
      price: parseFloat(currentService.price) || 0, // Can be 0 in quotation mode
      quantity: parseInt(currentService.quantity) || 1,
    };

    setServices([...services, newService]);
    // Clear inputs after adding, keeping the phone model for the next service
    setSelectedServiceOption(null);
    setCurrentService({ customName: '', model: currentService.model, serialNumber: '', price: '', quantity: 1 });
  };

  // Remove service
  const handleRemoveService = (id) => {
    setServices(services.filter(s => s.id !== id));
  };

  // Update service quantity
  const handleUpdateQuantity = (id, quantity) => {
    setServices(services.map(s => 
      s.id === id ? { ...s, quantity: Math.max(1, parseInt(quantity) || 1) } : s
    ));
  };

  // Apply discount
  const handleApplyDiscount = () => {
    const value = parseFloat(discountInputVal);
    if (!value || value <= 0) {
      toast.error(t('invalid_discount') || 'Enter a valid discount value');
      return;
    }

    let amount = 0;
    if (discountInputType === 'percentage') {
      if (value > 100) {
        toast.error(t('discount_max_100') || 'Percentage cannot exceed 100%');
        return;
      }
      amount = (originalTotal * value) / 100;
    } else {
      amount = value;
      if (amount > originalTotal) {
        toast.error(t('discount_exceeds_total') || 'Discount cannot exceed total');
        return;
      }
    }

    setDiscount({ type: discountInputType, value, amount });
    setShowDiscountForm(false);
    setDiscountInputVal('');
    toast.success(t('discount_applied') || 'Discount applied');
  };

  // Remove discount
  const handleRemoveDiscount = () => {
    setDiscount(null);
    setDiscountInputVal('');
    toast.success(t('discount_removed') || 'Discount removed');
  };

  // Handle print quotation
  const handlePrintQuotation = () => {
    if (!customerName.trim()) {
      toast.error(t('customer_name_required') || 'Please enter customer name');
      return;
    }
    if (services.length === 0) {
      toast.error(t('at_least_one_service_required') || 'Please add at least one service');
      return;
    }
    if (!pickupDate) {
      toast.error(t('pickup_date_required') || 'Please select a pickup date');
      return;
    }
    
    // Print using hidden iframe (browser print dialog on same page)
    try {
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'absolute';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = 'none';
      
      document.body.appendChild(printFrame);
      
      printFrame.srcdoc = previewHtml;
      
      printFrame.onload = () => {
        setTimeout(() => {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
          
          setTimeout(() => {
            document.body.removeChild(printFrame);
            toast.success(t('quotation_ready_to_print') || 'Quotation is ready to print');
          }, 100);
        }, 250);
      };
    } catch (error) {
      console.error('Print error:', error);
      toast.error(t('print_failed') || 'Failed to print quotation');
    }
  };
  
  // Handle submit
  const handleSubmit = async () => {
    // If in quotation mode, just print
    if (isQuotationMode) {
      handlePrintQuotation();
      return;
    }
    
    if (!customerName.trim()) {
      toast.error(t('customer_name_required') || 'Please enter customer name');
      return;
    }
    if (services.length === 0) {
      toast.error(t('at_least_one_service_required') || 'Please add at least one service');
      return;
    }
    if (!pickupDate) {
      toast.error(t('pickup_date_required') || 'Please select a pickup date');
      return;
    }
    
    // ✅ Validate payment: if paid amount > 0, payment method is required
    if (paid > 0 && !selectedPaymentMethod && !useMultiplePayments) {
      toast.error(t('payment_method_required') || 'Please select a payment method');
      setPmError(true);
      return;
    }

    // ✅ Warning for unpaid repair tickets
    if (paid === 0 || !paidAmount) {
      setConfirmUnpaidDialog({
        open: true,
        onConfirm: () => {
          setConfirmUnpaidDialog({ open: false, onConfirm: null });
          submitRepairTicket();
        },
      });
      return;
    }

    // If payment exists, submit directly
    submitRepairTicket();
  };

  // Separate function to handle actual submission
  const submitRepairTicket = async () => {
    const repairTicketData = {
      ticketType: TICKET_TYPE.REPAIR,
      receiptNumber,
      pickupDate: new Date(pickupDate).toISOString(),
      services: services.map(s => ({
        Name: s.name,
        SerialNumber: s.serialNumber,
        Quantity: s.quantity,
        Price: s.price,
        VAT: customVATRate,
      })),
      customer: customer || {
        Name: customerName,
        PhoneNumber: customerPhone,
      },
      customerName,
      customerPhone,
      // Only set payment method if there's a paid amount
      paymentMethod: paid > 0 ? selectedPaymentMethod : null,
      paidAmount: paid > 0 ? paid : null,
      notes: notes.trim(),
      subtotal,
      discountAmount,
      vatAmount: totalVAT,
      total: totalIncl,
      paymentStatus: paymentStatusValue,
      isTvaIncluded,
      customVATRate,
      discount,
      templateHtml: previewHtml,
      remainingAmount: remaining,
      // ✅ Add multiple payment support
      useMultiplePayments,
      paymentMethodsArray: localPMArray,
    };

    // âœ… Wait for submission to complete
    try {
      await onSubmit(repairTicketData);
      // âœ… Only show success and reset if submission succeeded
      // Note: onSubmit should throw an error if it fails
    
    // Reset form
      // Reset form
      setServices([]);
      setSelectedServiceOption(null);
      setCurrentService({ customName: '', model: '', serialNumber: '', price: '', quantity: 1 });
      setPaidAmount('');
      setNotes('');
      setCustomerName('');
      setCustomerPhone('');
      const date = new Date();
      date.setDate(date.getDate() + 3);
      date.setHours(12, 0, 0, 0);
      setPickupDate(date.toISOString().slice(0, 16));
      setDiscount(null);
      setShowDiscountForm(false);
      setDiscountInputVal('');
      setUseMultiplePayments(false);
      setLocalPMArray([]);
      setPmError(false);
    } catch (error) {
      // Error is already handled in PosListPage, just log it

    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  const maxDateStr = maxDate.toISOString().slice(0, 10);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => { 
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
      container={container}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100%' : '92vh',
          height: isMobile ? '100%' : '92vh',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1500, // Higher z-index to appear above customer dialog
        },
      }}
      sx={{
        zIndex: 1500, // Ensure dialog itself has high z-index
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
          color: 'white',
          px: 3,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BuildIcon sx={{ fontSize: 22 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, fontSize: '1.05rem' }}>
              {t('create_repair_ticket') || 'Create Repair Ticket'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
              {t('repair_service_management') || 'Repair Service Management'}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'white', '&:hover': { bgcolor: alpha('#fff', 0.15) } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Two-Panel Body */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {/* LEFT PANEL - Form */}
        <Box
          sx={{
            flex: isMobile ? 'none' : '0 0 58%',
            width: isMobile ? '100%' : undefined,
            display: 'flex',
            flexDirection: 'column',
            borderRight: isMobile ? 'none' : '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            bgcolor: 'white',
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2.5,
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#06b6d4', 0.2), borderRadius: 3 },
            }}
          >
            {/* Quotation Mode Toggle */}
            <Box sx={{ mb: 2 }}>
              <Paper
                elevation={isQuotationMode ? 3 : 1}
                sx={{
                  p: 1.5,
                  bgcolor: isQuotationMode ? '#fef3c7' : '#f0fdfa',
                  border: '2px solid',
                  borderColor: isQuotationMode ? '#f59e0b' : alpha('#06b6d4', 0.2),
                  borderRadius: 1.5,
                  transition: 'all 0.3s ease'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon sx={{ fontSize: 18, color: isQuotationMode ? '#f59e0b' : '#06b6d4' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: isQuotationMode ? '#92400e' : '#0e7490' }}>
                        {isQuotationMode ? (t('quotation_mode') || 'Quotation Mode') : (t('repair_ticket_mode') || 'Repair Ticket Mode')}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        {isQuotationMode 
                          ? (t('quotation_mode_desc') || 'Quotation mode is enabled: add services without amounts and print an estimate.')
                          : (t('repair_ticket_mode_desc') || 'Not sure about the service cost? Enable Quotation mode to create the ticket without entering an amount.')
                        }
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isQuotationMode}
                        onChange={(e) => {
                          setIsQuotationMode(e.target.checked);
                          if (e.target.checked) {
                            // Clear payment when switching to quotation mode
                            setPaidAmount('');
                            setSelectedPaymentMethod(null);
                            setUseMultiplePayments(false);
                            setLocalPMArray([]);
                            toast.info(t('quotation_mode_enabled') || 'Quotation mode enabled - prices are now optional');
                          }
                        }}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#f59e0b',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            bgcolor: '#f59e0b',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                        {t('quotation') || 'Quotation'}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
              </Paper>
            </Box>

            {/* Customer Section */}
            <Box sx={{ mb: 2.5 }}>
              <SectionHeader icon={PersonIcon} label={t('customer_information') || 'Customer Info'} />
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={<>{t('customer_name') || 'Customer Name'} <span style={{ color: '#ef4444' }}>*</span></>}
                    value={customerName}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 20) {
                        setCustomerName(value);
                      }
                    }}
                    placeholder={t('enter_customer_name') || 'Enter name'}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          {customerName && (
                            <IconButton
                              size="small"
                              onClick={() => setCustomerName('')}
                              sx={{ mr: 0.5 }}
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                          {onOpenCustomerDialog && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={onOpenCustomerDialog}
                              sx={{ 
                                textTransform: 'none', 
                                fontSize: '0.7rem',
                                minWidth: 'auto',
                                px: 1,
                                py: 0.5,
                                borderColor: '#06b6d4',
                                color: '#06b6d4',
                                '&:hover': {
                                  borderColor: '#0891b2',
                                  bgcolor: alpha('#06b6d4', 0.05),
                                }
                              }}
                            >
                              {t('search_existing') || 'Search'}
                            </Button>
                          )}
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ maxLength: 20 }}
                    helperText={`${customerName.length}/20`}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('customer_phone') || 'Phone Number'}
                    value={customerPhone}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow numeric characters
                      if (/^\d*$/.test(value)) {
                        setCustomerPhone(value);
                      }
                    }}
                    placeholder="+32 xxx xx xx xx"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label={<>{t('pickup_date_time') || 'Pickup Date & Time'} <span style={{ color: '#ef4444' }}>*</span></>}
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><CalendarIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Services Section */}
            <Box sx={{ mb: 2.5 }}>
              <SectionHeader 
                icon={BuildIcon} 
                label={<>{t('services') || 'Services'} <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span></>} 
                action={
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isTvaIncluded}
                        onChange={(e) => setIsTvaIncluded(e.target.checked)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#06b6d4',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            bgcolor: '#06b6d4',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                        {isTvaIncluded ? (t('vat_included') || 'VAT Incl.') : (t('vat_excluded') || 'VAT Excl.')}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                }
              />
              
              {/* Add Service Form */}
              <Paper sx={{ p: 1.5, mb: 1.5, bgcolor: '#f0fdfa', borderRadius: 1.5, border: '1px solid', borderColor: alpha('#06b6d4', 0.2) }}>
                {serviceError && (
                  <Box sx={{ mb: 1.5, p: 1, bgcolor: '#fee2e2', borderRadius: 1, border: '1px solid #fca5a5' }}>
                    <Typography variant="body2" sx={{ color: '#dc2626', fontWeight: 600, fontSize: '0.8rem' }}>
                       {serviceError}
                    </Typography>
                  </Box>
                )}
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      size="small"
                      options={serviceOptions}
                      value={selectedServiceOption}
                      onChange={handleServiceSelect}
                      groupBy={(option) => option.group}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.label === value.label}
                      clearOnEscape
                      noOptionsText={t('no_services_found') || 'No services found'}
                      slotProps={{
                        popper: { sx: { zIndex: 1700 } },
                        paper: {
                          sx: {
                            maxHeight: 420,
                            mt: 0.5,
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: alpha('#06b6d4', 0.25),
                            borderRadius: 1.5,
                            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.16)',
                          },
                        },
                        listbox: {
                          sx: {
                            p: 0,
                            '& .MuiAutocomplete-option': {
                              minHeight: 40,
                              px: 1.5,
                              py: 0.65,
                              ml: 2.5,
                              mr: 1,
                              borderRadius: 0.75,
                              borderBottom: '1px solid',
                              borderColor: alpha('#cbd5e1', 0.5),
                              transition: 'background-color 0.15s ease, padding-left 0.15s ease',
                              '&:hover, &[aria-selected="true"]': {
                                bgcolor: alpha('#06b6d4', 0.07),
                              },
                              '&.Mui-focused': {
                                bgcolor: alpha('#06b6d4', 0.12),
                                pl: 1.75,
                              },
                              '&:last-child': { borderBottom: 'none' },
                            },
                          },
                        },
                      }}
                      renderGroup={(params) => (
                        <li key={params.key}>
                          <Box
                            sx={{
                              position: 'sticky',
                              top: 0,
                              zIndex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 1.5,
                              py: 0.75,
                              bgcolor: '#f8fafc',
                              borderBottom: '1px solid',
                              borderColor: alpha('#cbd5e1', 0.7),
                            }}
                          >
                            <Typography component="span" sx={{ fontSize: '0.95rem', lineHeight: 1, opacity: 0.75 }}>
                              {groupIcons[params.group]}
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 900,
                                fontSize: '0.72rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.8px',
                                color: '#64748b',
                              }}
                            >
                              {params.group}
                            </Typography>
                          </Box>
                          <Box component="ul" sx={{ p: 0, m: 0 }}>{params.children}</Box>
                        </li>
                      )}
                      renderOption={(props, option, { selected }) => (
                        <Box component="li" {...props} key={`${option.group}-${option.label}`}>
                          <Box
                            aria-hidden="true"
                            sx={{
                              width: 4,
                              height: 24,
                              flexShrink: 0,
                              borderRadius: 2,
                              bgcolor: option.isOther ? '#f59e0b' : '#06b6d4',
                            }}
                          />
                          <Typography
                            component="span"
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              ml: 1.25,
                              color: option.isOther ? '#9a3412' : '#1e293b',
                              fontSize: '0.76rem',
                              fontWeight: selected ? 800 : 600,
                              lineHeight: 1.3,
                            }}
                          >
                            {option.label}
                          </Typography>
                          {selected && (
                            <Typography
                              component="span"
                              aria-hidden="true"
                              sx={{ color: '#0891b2', fontSize: '0.95rem', fontWeight: 800, ml: 1 }}
                            >
                              ✓
                            </Typography>
                          )}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={<>{t('service') || 'Service'} <span style={{ color: '#ef4444' }}>*</span></>}
                          placeholder={t('search_service') || 'Search a service...'}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label={<>{t('phone_model') || 'Phone Model'} <span style={{ color: '#ef4444' }}>*</span></>}
                      value={currentService.model}
                      onChange={handlePhoneModelChange}
                      placeholder={t('enter_phone_model') || 'e.g. 17 Pro Max, Samsung Galaxy S26 Ultra'}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                        endAdornment: currentService.model && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setCurrentService({ ...currentService, model: '' })}
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {selectedServiceOption?.isOther && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label={<>{t('custom_service_name') || 'Custom Service Name'} <span style={{ color: '#ef4444' }}>*</span></>}
                        value={currentService.customName}
                        onChange={(e) => {
                          setCurrentService({ ...currentService, customName: e.target.value });
                          setServiceError('');
                        }}
                        placeholder={t('describe_service') || 'Describe the repair service'}
                      />
                    </Grid>
                  )}
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('imei_serial_number') || 'IMEI / Serial Number (optional)'}
                      value={currentService.serialNumber}
                      onChange={(e) => setCurrentService({ ...currentService, serialNumber: e.target.value })}
                      inputProps={{ maxLength: 20 }}
                      InputProps={{
                        endAdornment: currentService.serialNumber && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setCurrentService({ ...currentService, serialNumber: '' })}
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={
                        <>
                          {isTvaIncluded ? (t('price_incl_vat') || 'Price (incl. VAT)') : (t('price_excl_vat') || 'Price (excl. VAT)')}
                          {!isQuotationMode && <span style={{ color: '#ef4444' }}> *</span>}
                          {isQuotationMode && <span style={{ color: '#f59e0b', fontSize: '0.7rem', marginLeft: 4 }}>({t('optional') || 'Optional'})</span>}
                        </>
                      }
                      value={currentService.price}
                      onChange={(e) => {
                        setCurrentService({ ...currentService, price: e.target.value });
                        setServiceError(''); // Clear error when typing
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      placeholder={isQuotationMode ? (t('optional') || 'Optional') : ''}
                    />
                  </Grid>
                  
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={t('quantity') || 'Qty'}
                      value={currentService.quantity}
                      onChange={(e) => setCurrentService({ ...currentService, quantity: e.target.value })}
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddService}
                      sx={{
                        background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                        height: '40px',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
                        },
                      }}
                    >
                      {t('add') || 'Add'}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {/* Services List */}
              {services.length > 0 ? (
                <Paper
                  variant="outlined"
                  sx={{ borderRadius: 1.5, overflow: 'hidden', borderColor: alpha('#06b6d4', 0.25) }}
                >
                  {services.map((service, index) => (
                    <Box
                      key={service.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 1.5,
                        py: 1.1,
                        bgcolor: index % 2 === 0 ? 'white' : '#f8fafc',
                        borderTop: index === 0 ? 'none' : '1px solid',
                        borderColor: 'divider',
                        transition: 'background-color 0.15s ease',
                        '&:hover': { bgcolor: alpha('#06b6d4', 0.06) },
                      }}
                    >
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha('#06b6d4', 0.12),
                          color: '#0e7490',
                        }}
                      >
                        <BuildIcon sx={{ fontSize: 15 }} />
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#0f172a', lineHeight: 1.3 }}
                          noWrap
                          title={service.name}
                        >
                          {service.name}
                        </Typography>
                        {service.serialNumber && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem', display: 'block' }}
                            noWrap
                            title={service.serialNumber}
                          >
                            IMEI / Serial: {service.serialNumber}
                          </Typography>
                        )}
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexShrink: 0,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          overflow: 'hidden',
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleUpdateQuantity(service.id, service.quantity - 1)}
                          disabled={service.quantity <= 1}
                          sx={{ borderRadius: 0, px: 0.5 }}
                        >
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, lineHeight: 1 }}>−</Typography>
                        </IconButton>
                        <Typography
                          sx={{ minWidth: 22, textAlign: 'center', fontSize: '0.8rem', fontWeight: 600 }}
                        >
                          {service.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleUpdateQuantity(service.id, service.quantity + 1)}
                          sx={{ borderRadius: 0, px: 0.5 }}
                        >
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, lineHeight: 1 }}>+</Typography>
                        </IconButton>
                      </Box>

                      <Box sx={{ flexShrink: 0, textAlign: 'right', minWidth: 64 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0e7490', lineHeight: 1.2 }}>
                          €{(service.price * service.quantity).toFixed(2)}
                        </Typography>
                        {service.quantity > 1 && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            €{service.price.toFixed(2)} × {service.quantity}
                          </Typography>
                        )}
                      </Box>

                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveService(service.id)}
                        sx={{ flexShrink: 0 }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ))}

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1.5,
                      py: 1,
                      bgcolor: alpha('#06b6d4', 0.08),
                      borderTop: '1px solid',
                      borderColor: alpha('#06b6d4', 0.25),
                    }}
                  >
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0e7490', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {services.length} {services.length === 1 ? (t('service') || 'Service') : (t('services') || 'Services')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: '#0e7490' }}>
                      €{services.reduce((sum, s) => sum + s.price * s.quantity, 0).toFixed(2)}
                    </Typography>
                  </Box>
                </Paper>
              ) : (
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdfa' }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('no_services_added') || 'No services added yet'}
                  </Typography>
                </Paper>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Payment Section - Hidden in quotation mode */}
            {!isQuotationMode && (
              <Box sx={{ mb: 2.5 }}>
                <SectionHeader icon={PaymentIcon} label={t('payment') || 'Payment'} />
              
              {/* Multi-payment toggle */}
              <Box sx={{ mb: 1.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useMultiplePayments}
                      onChange={(e) => {
                        setUseMultiplePayments(e.target.checked);
                        if (e.target.checked) {
                          setSelectedPaymentMethod(null);
                          setPmError(false);
                        } else {
                          setLocalPMArray([]);
                        }
                      }}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#06b6d4',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          bgcolor: '#06b6d4',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      {t('multi_payment') || 'Multiple Payment Methods'}
                    </Typography>
                  }
                  sx={{ mb: 0 }}
                />
              </Box>

              {!useMultiplePayments ? (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={t('paid_amount') || 'Paid Amount'}
                      value={paidAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPaidAmount(value);
                        // Clear payment method if amount is cleared or set to 0
                        if (!value || parseFloat(value) === 0) {
                          setSelectedPaymentMethod(null);
                          setPmError(false);
                        } else {
                          setPmError(!selectedPaymentMethod);
                        }
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText={
                        paid > 0 && remaining > 0
                          ? `${t('remaining') || 'Remaining'}: €${remaining.toFixed(2)}`
                          : paid > 0 && remaining <= 0
                          ? `${t('fully_paid') || 'Fully Paid'}`
                          : `${t('total') || 'Total'}: €${totalIncl.toFixed(2)}`
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      error={pmError || (paid > 0 && !selectedPaymentMethod)}
                      disabled={!paidAmount || parseFloat(paidAmount) === 0}
                    >
                      <InputLabel>{t('payment_method') || 'Payment Method'}</InputLabel>
                      <Select
                        value={selectedPaymentMethod?.id || ''}
                        onChange={(e) => {
                          const pm = paymentMethods.find(p => p.id === e.target.value);
                          setSelectedPaymentMethod(pm);
                          setPmError(false);
                        }}
                        label={t('payment_method') || 'Payment Method'}
                        disabled={!paidAmount || parseFloat(paidAmount) === 0}
                        MenuProps={{
                          sx: { zIndex: 1600 },
                          PaperProps: { sx: { zIndex: 1600 } }
                        }}
                      >
                        {paymentMethods.map((pm) => (
                          <MenuItem key={pm.id} value={pm.id}>
                            {pm.label || pm.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {paid > 0 && !selectedPaymentMethod ? (
                        <FormHelperText>{t('payment_method_required') || 'Please select a payment method'}</FormHelperText>
                      ) : (!paidAmount || parseFloat(paidAmount) === 0) ? (
                        <FormHelperText>{t('enter_amount_first') || 'Enter payment amount first'}</FormHelperText>
                      ) : null}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('vat_rate') || 'VAT Rate (%)'}</InputLabel>
                      <Select
                        value={customVATRate ?? 21}
                        onChange={(e) => setCustomVATRate(Number(e.target.value))}
                        label={t('vat_rate') || 'VAT Rate (%)'}
                        MenuProps={{
                          sx: { zIndex: 1700 },
                          PaperProps: { sx: { zIndex: 1700 } },
                        }}
                      >
                        {vatRateOptions.map((rate) => (
                          <MenuItem key={rate} value={rate} sx={{ fontSize: '0.78rem' }}>
                            {rate}%
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {discount ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#ecfdf5', p: 1.5, borderRadius: 1, height: '40px' }}>
                        <DiscountIcon sx={{ fontSize: 18, color: '#059669' }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                            {discount.type === 'percentage' ? `${discount.value}%` : `€${discount.value.toFixed(2)}`} {t('discount') || 'Discount'}
                          </Typography>
                        </Box>
                        <IconButton size="small" color="error" onClick={handleRemoveDiscount}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<DiscountIcon />}
                        onClick={() => setShowDiscountForm(!showDiscountForm)}
                        sx={{ height: '40px', textTransform: 'none' }}
                      >
                        {t('add_discount') || 'Add Discount'}
                      </Button>
                    )}
                  </Grid>
                </Grid>
              ) : (
                <Box>
                  <MultiplePaymentMethodsSection
                    paymentMethods={localPMArray}
                    onPaymentMethodsChange={setLocalPMArray}
                    totalAmount={totalIncl}
                    disabled={totalIncl <= 0}
                    paymentMethodOptions={paymentMethods.map((pm) => ({ value: pm.id, label: pm.label || pm.name }))}
                  />
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{t('vat_rate') || 'VAT Rate (%)'}</InputLabel>
                        <Select
                          value={customVATRate ?? 21}
                          onChange={(e) => setCustomVATRate(Number(e.target.value))}
                          label={t('vat_rate') || 'VAT Rate (%)'}
                          MenuProps={{
                            sx: { zIndex: 1700 },
                            PaperProps: { sx: { zIndex: 1700 } },
                          }}
                        >
                          {vatRateOptions.map((rate) => (
                            <MenuItem key={rate} value={rate} sx={{ fontSize: '0.78rem' }}>
                              {rate}%
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {discount ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#ecfdf5', p: 1.5, borderRadius: 1, height: '40px' }}>
                          <DiscountIcon sx={{ fontSize: 18, color: '#059669' }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                              {discount.type === 'percentage' ? `${discount.value}%` : `€${discount.value.toFixed(2)}`} {t('discount') || 'Discount'}
                            </Typography>
                          </Box>
                          <IconButton size="small" color="error" onClick={handleRemoveDiscount}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<DiscountIcon />}
                          onClick={() => setShowDiscountForm(!showDiscountForm)}
                          sx={{ height: '40px', textTransform: 'none' }}
                        >
                          {t('add_discount') || 'Add Discount'}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {/* Discount Form */}
              {showDiscountForm && !discount && (
                <Paper sx={{ p: 1.5, mt: 1.5, bgcolor: '#f0fdfa', borderRadius: 1.5 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <ToggleButtonGroup
                        value={discountInputType}
                        exclusive
                        onChange={(e, newType) => newType && setDiscountInputType(newType)}
                        size="small"
                        fullWidth
                      >
                        <ToggleButton value="percentage">
                          <PercentIcon sx={{ fontSize: 14, mr: 0.5 }} />
                          {t('percentage') || 'Percentage'}
                        </ToggleButton>
                        <ToggleButton value="fixed">€ {t('fixed') || 'Fixed'}</ToggleButton>
                      </ToggleButtonGroup>
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={discountInputType === 'percentage' ? t('discount_percentage') || 'Discount %' : t('discount_amount') || 'Amount'}
                        value={discountInputVal}
                        onChange={(e) => setDiscountInputVal(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{discountInputType === 'percentage' ? '%' : '€'}</InputAdornment>,
                        }}
                        inputProps={{ min: 0, step: discountInputType === 'percentage' ? 1 : 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleApplyDiscount}
                        sx={{ 
                          height: '40px',
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        }}
                      >
                        {t('apply') || 'Apply'}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              )}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Notes Section */}
            <Box sx={{ mb: 2.5 }}>
              <SectionHeader icon={NotesIcon} label={t('notes') || 'Notes'} />
              <Paper sx={{ p: 1.5, bgcolor: '#f0fdfa', borderRadius: 1.5, border: '1px solid', borderColor: alpha('#06b6d4', 0.2) }}>
                <Grid container spacing={1.5}>
                {noteTemplates.length > 0 && (
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('note_template') || 'Note Template'}</InputLabel>
                      <Select
                        value={selectedNoteTemplate}
                        onChange={handleNoteTemplateChange}
                        label={t('note_template') || 'Note Template'}
                        MenuProps={{
                          sx: { zIndex: 1600 },
                          PaperProps: { sx: { zIndex: 1600 } }
                        }}
                      >
                        <MenuItem value="">
                          <em>{t('none') || 'None'}</em>
                        </MenuItem>
                        {noteTemplates.map((template) => (
                          <MenuItem key={template.Id} value={template.Id}>
                            {template.TemplateName}
                            {template.IsDefault && ` (${t('default') || 'Default'})`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    label={t('notes_optional') || 'Notes (optional)'}
                    value={notes}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 1000) {
                        setNotes(value);
                      }
                    }}
                    placeholder={t('enter_custom_notes') || 'Enter custom notes here...'}
                    inputProps={{ maxLength: 1000 }}
                    helperText={`${notes.length}/1000`}
                  />
                </Grid>
              </Grid>
              </Paper>
            </Box>
          </Box>

          {/* Bottom Action Buttons */}
          <Box sx={{ flexShrink: 0, p: 2.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={onClose}
                  sx={{ py: 1.25 }}
                >
                  {t('cancel') || 'Cancel'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={isQuotationMode ? <ReceiptIcon /> : <BuildIcon />}
                  onClick={handleSubmit}
                  disabled={services.length === 0 || !customerName.trim()}
                  sx={{
                    py: 1.25,
                    background: isQuotationMode 
                      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                      : 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                    '&:hover': {
                      background: isQuotationMode
                        ? 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                        : 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
                    },
                  }}
                >
                  {isQuotationMode ? (t('print_quotation') || 'Print Quotation') : (t('create') || 'Create')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* RIGHT PANEL - Preview */}
        <Box
          sx={{
            flex: isMobile ? 'none' : '0 0 42%',
            width: isMobile ? '100%' : undefined,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#dde1e7',
            minHeight: isMobile ? 300 : 0,
          }}
        >
          {/* Preview Title */}
          <Box
            sx={{
              flexShrink: 0,
              px: 1.5,
              py: 0.75,
              bgcolor: 'white',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <ReceiptIcon sx={{ fontSize: 14, color: '#06b6d4' }} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}
              >
                {t('ticket_preview') || 'Ticket Preview'}
              </Typography>
            </Box>
          </Box>

          {/* Preview Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#06b6d4', 0.25), borderRadius: 2 },
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                width: '302px',
                bgcolor: 'white',
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                borderRadius: 1,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <iframe
                key={`${services.length}-${discountAmount}`}
                title="ticket-preview"
                srcDoc={previewHtml}
                scrolling="no"
                style={{
                  width: '302px',
                  height: iframeHeight,
                  border: 'none',
                  display: 'block',
                }}
                onLoad={(e) => {
                  const iframe = e.target;
                  setTimeout(() => {
                    try {
                      const doc = iframe.contentDocument;
                      const container = doc.querySelector('.receipt-container');
                      const bodyPad = 20;
                      const h = container
                        ? container.offsetHeight + bodyPad
                        : Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
                      if (h > 100) setIframeHeight(h + 32);
                    } catch {
                      setIframeHeight(Math.max(800, services.length * 80 + 600));
                    }
                  }, 300);
                }}
                sandbox="allow-same-origin"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Unpaid Repair Ticket Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmUnpaidDialog.open}
        title={t('confirm_unpaid_ticket') || 'Confirm Unpaid Repair Ticket'}
        message={
          t('confirm_unpaid_repair_ticket_detailed') ||
          `⚠️ No payment recorded for this repair ticket.\n\nThe ticket will be created with status "${PAYMENT_STATUS.NOT_PAID}" (€${totalIncl.toFixed(2)} due).\n\nCustomer: ${customerName}\nPickup Date: ${new Date(pickupDate).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}\n\nDo you want to proceed without payment?`
        }
        onConfirm={confirmUnpaidDialog.onConfirm}
        onCancel={() => {
          setConfirmUnpaidDialog({ open: false, onConfirm: null });
          toast.info(t('operation_cancelled') || 'Operation cancelled. Please add payment details.');
        }}
        confirmText={t('proceed_without_payment') || 'Proceed Without Payment'}
        cancelText={t('cancel') || 'Cancel'}
        variant="warning"
        container={container}
        zIndex={1600}
      />
    </Dialog>
  );
}

export default RepairTicketDialog;